import { Capacitor } from '@capacitor/core'
import type { Ref } from 'vue'
import { ref } from 'vue'

import { cashMachineStatus } from '@/plugins/cashMachineStatus'
import type { Config } from '@/types/config'

import Cashdro from './cashdro'
import CashGuard from './cashguard'
import CashKeeper from './cashkeeper'
import Cashlogy from './cashlogy'
import DevCashMachine from './devcashmachine'
import Glory from './glory'
import type { ICashMachine } from './types'

class CashMachine {
  private hasStatus: boolean = false
  private machine!: ICashMachine

  public methods = ['cashdro', 'cashlogy', 'cashguard', 'glory', 'cashkeeper']
  public method: string | null = null

  public inputAmount: Ref<number>
  public totalAmount: Ref<number>

  constructor() {
    this.inputAmount = ref(0)
    this.totalAmount = ref(0)
  }

  async init(config: Config, preferredConfigId?: string) {
    const inputAmountListener = this.inputAmountListener.bind(this)
    const totalAmountListener = this.totalAmountListener.bind(this)

    const cashMachines = {
      Cashdro,
      Cashlogy,
      CashGuard,
      Glory,
      CashKeeper
    } as const

    for (const method of config.paymentMethodsConfig) {
      if (!method.configs) continue
      for (const methodConfig of method.configs) {
        if (
          methodConfig.id === preferredConfigId ||
          (!preferredConfigId && (methodConfig.ip || methodConfig.port))
        ) {
          if (Capacitor.getPlatform() === 'web') {
            DevCashMachine.init({ inputAmountListener, totalAmountListener })
            this.machine = DevCashMachine
            this.hasStatus = true
          } else {
            await cashMachines[method.name as keyof typeof cashMachines].init({
              ip: methodConfig.ip,
              port: methodConfig.port,
              name: methodConfig.name,
              password: methodConfig.password,
              username: methodConfig.username,
              type: methodConfig.type,
              inputAmountListener,
              totalAmountListener
            })
            this.machine = cashMachines[method.name as keyof typeof cashMachines]
            this.hasStatus = true
          }
          this.method = method.name.toLowerCase()
          return
        }
      }
    }
  }

  private inputAmountListener(amount: number) {
    this.inputAmount.value = amount
  }

  private totalAmountListener(amount: number) {
    this.totalAmount.value = amount
  }

  private onCancel() {
    return this.machine.cancel()
  }

  async charge(amount: number): Promise<number> {
    let component = null
    try {
      if (this.hasStatus) {
        component = cashMachineStatus({
          inputAmount: this.inputAmount,
          amount,
          onCancel: this.onCancel.bind(this)
        })
      }
      return await this.machine.charge(amount)
    } finally {
      component?.close()
    }
  }

  async payIn(amount: number): Promise<number> {
    let component = null
    let res = null
    try {
      if (this.hasStatus) {
        component = cashMachineStatus({
          inputAmount: this.inputAmount,
          amount,
          onCancel: this.onCancel.bind(this)
        })
      }
      res = await this.machine.payIn(amount)
    } finally {
      component?.close()
    }
    return res
  }

  async payOut(amount: number): Promise<number> {
    return this.machine.payOut(amount)
  }

  async close(): Promise<void> {
    if (!this.machine.close) return
    return this.machine.close()
  }

  async refreshTotalAmount(): Promise<void> {
    if (!this.machine.refreshTotalAmount) return
    return this.machine.refreshTotalAmount()
  }

  isEnabled(): boolean {
    return !!this.machine
  }

  async openBackoffice(): Promise<void> {
    if (!this.machine.openBackoffice) return
    await this.machine.openBackoffice()
  }
}

export default new CashMachine()
